import type { Character } from "@cloudnest/horror-race-engine";
import { CoinHand, type PlacementController } from "./CoinHand";
import { DrawnCoinsPreview } from "./DrawnCoinsPreview";
import { Button } from "./ui/button";

interface ActionPanelProps {
  readonly isMyTurn: boolean;
  readonly currentPlayerName: string;
  readonly drawnCoins: readonly Character[];
  /** True after the active player chose 뽑기 but the coins have not arrived yet. */
  readonly drawPending: boolean;
  readonly disabled: boolean;
  readonly canForfeit: boolean;
  readonly onRequestDraw: () => void;
  /** T23-b placement draft controller (active player's coin tray). */
  readonly placement: PlacementController;
  readonly onForfeit: () => void;
}

/**
 * T5/T9/T24 — action content. Rendered as the body of the bottom action surface
 * (chrome + bottom-anchoring live on the GameScreen wrapper, below the 내 선택
 * strip — T15 v2 / T24). The status line is persistent (always visible); the
 * dynamic block (drawn coins + buttons) slides in/out and collapses, so when
 * there's nothing to act on the persistent strip + status drop to the screen
 * bottom and stay in view. On your turn it first offers an explicit 토큰 포기 /
 * 뽑기 choice (T9, 긍정 우측) — the coin hand only appears after the host has
 * drawn (drawnCoins non-empty), never auto-drawn.
 */
export function ActionPanel({
  isMyTurn,
  currentPlayerName,
  drawnCoins,
  drawPending,
  disabled,
  canForfeit,
  onRequestDraw,
  placement,
  onForfeit,
}: ActionPanelProps) {
  const hasCoins = drawnCoins.length > 0;

  // While the active player is placing, the status guides the current step.
  const placedCount = drawnCoins.reduce(
    (n, _, i) => n + (placement.assignments[i] != null ? 1 : 0),
    0,
  );
  const placingStatus =
    placement.activeCoinIndex !== null
      ? "존을 탭해서 놓으세요"
      : placement.allAssigned
        ? "모두 배치됨 — 배치 확정하세요"
        : placedCount > 0
          ? `코인을 배치하세요 (${placedCount}/${drawnCoins.length})`
          : "코인을 탭해서 집으세요";

  const status = isMyTurn
    ? hasCoins
      ? placingStatus
      : drawPending
        ? "코인을 뽑는 중…"
        : "당신의 차례입니다"
    : hasCoins
      ? `${currentPlayerName}님이 배치 중…`
      : `${currentPlayerName}님의 차례`;

  // T24 — the dynamic block (③ coins + ④ buttons) is shown only when there's
  // something to act on; otherwise it collapses (slides down) so the persistent
  // 내 선택 strip + status drop to the screen bottom and stay visible.
  const showDynamic = isMyTurn || hasCoins;

  return (
    <div
      data-testid="horror-action-panel"
      className="px-4 pt-2.5 pb-[max(0.625rem,env(safe-area-inset-bottom))]"
    >
      {/* T24 — compact, top-aligned 4단: 내 선택(strip, GameScreen) → 상태 → 코인 → 버튼.
          No min-height / no bottom-anchor; the bar hugs the screen bottom so the
          last shown 단 lands at the bottom edge. */}
      <div className="mx-auto flex max-w-[600px] flex-col gap-2">
        {/* ② turn status — persistent: always visible. When the dynamic block
            below collapses, the bar shrinks and this (with the 내 선택 strip)
            drops to the screen bottom and stays in view. */}
        <span
          data-testid="horror-action-status"
          className="text-center font-semibold text-sm"
          style={{
            color: isMyTurn
              ? "var(--cn-horror-round-text)"
              : "var(--cn-horror-text-muted)",
          }}
        >
          {status}
        </span>

        {/* ③ coins + ④ buttons — dynamic block. Slides up to appear / down to
            disappear (height collapse + fade). overflow-hidden enables the
            collapse; the inner padding clears the chip ring/badge overflow
            (inset -4 -7 / top -7) + the button glow. reduced-motion → instant. */}
        <div
          className={`flex flex-col gap-2 overflow-hidden px-3 pt-2.5 pb-2.5 transition-all duration-200 ease-out motion-reduce:transition-none ${
            showDynamic
              ? "max-h-[18rem] translate-y-0 opacity-100"
              : "max-h-0 translate-y-3 opacity-0"
          }`}
        >
          {!isMyTurn ? (
            // ③ waiting — read-only drawn coins (no controls).
            hasCoins ? (
              <DrawnCoinsPreview coins={drawnCoins} />
            ) : null
          ) : hasCoins ? (
            // ③ + ④ — tap-to-place chip tray + controls (T23-b).
            <CoinHand
              coins={drawnCoins}
              placement={placement}
              disabled={disabled}
            />
          ) : (
            // ④ pre-draw choice (T9) — 포기 좌측 · 뽑기(긍정) 우측 (T24).
            <div data-testid="horror-draw-choice" className="flex gap-2">
              {canForfeit && (
                <Button
                  data-testid="forfeit-btn"
                  onClick={onForfeit}
                  disabled={disabled || drawPending}
                  variant="danger"
                  className="flex-1"
                >
                  토큰 포기
                </Button>
              )}
              <Button
                data-testid="draw-btn"
                onClick={onRequestDraw}
                disabled={disabled || drawPending}
                variant="primary"
                className="flex-1"
              >
                뽑기
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
